import * as React from "react";
import { Timeline, TimelineEvent } from "react-event-timeline";
import Collapsible from "react-collapsible";
import { useHistory } from "react-router-dom";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { KeyedVisitSessionItems } from "./StartSessionWizardReducer";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Phases } from "../../misc/api/ApiPhases";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { StudyScheduleVisit } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { StudyScheduleSession } from "webportalclient";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { Subject } from "../../misc/api/apiTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";

interface VisitOptionProps {
    sessionItemId: string;
    sessionItemLabel: string;
    disabled: boolean;
}

interface VisitSelectionProps {
    subject: Subject;
    sessionType: SessionTypes;
    selectedItems: string[];
    currentVisitItems: StudyScheduleSession[];
    futureVisitItems: KeyedVisitSessionItems[];
    rollingVisitItems: StudyScheduleSession[];
    onSelect: (id: string, checked: boolean) => void;
    onSettingOutstandingVisits: (items: StudyScheduleVisit[]) => void;
    onSettingCurrentVisitItems: (items: StudyScheduleSession[]) => void;
    onSettingFutureVisitItems: (items: KeyedVisitSessionItems[]) => void;
    onSettingRollingVisitItems: (items: StudyScheduleSession[]) => void;
    onSettingCandidates: (candidates: string[]) => void;
    onStartSession: () => void;
}

export const VisitSelection = ({ subject, sessionType, selectedItems, currentVisitItems, futureVisitItems, rollingVisitItems, onSelect, onSettingOutstandingVisits, onSettingCurrentVisitItems, onSettingFutureVisitItems, onSettingRollingVisitItems, onSettingCandidates, onStartSession }: VisitSelectionProps) => {
    const [loading, setLoading] = React.useState(false);
    const [alert, setAlert] = React.useState<string>();
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const history = useHistory();
    const legacyClient = useWebPortalLegacyClient();

    const SessionOption = ({ sessionItemId, sessionItemLabel, disabled }: VisitOptionProps) => {
        const selected = selectedItems.includes(sessionItemId);

        return (
            <><button disabled={disabled} className={selected ? "btn btn-success" : "btn btn-primary"} onClick={() => onSelect(sessionItemId, !selected)}>{sessionItemLabel}</button>&nbsp;</>
        );
    }

    const CollapsibleVisitHeader = ({ header, open }: { header: string; open: boolean }) => (
        <div className="row">
            <h3 className="col-md-6">Visit - {header}</h3>
            <div className="col-md-6">
                <span className="pull-right">
                    <FontAwesomeIcon icon={open ? faCaretUp : faCaretDown} />
                </span>
            </div>
        </div>
    )

    const FutureVisitItemWrapper = () => {
        if (futureVisitItems) {
            return (
                <>
                    {futureVisitItems.map((items) => {
                        const isOpen = items.value.some((i) => selectedItems.includes(i.id));
                        const iconClass = items.phase === Phases.Unscheduled ? "fas fa-calendar-exclamation" : "fas fa-calendar-edit";

                        return (<TimelineEvent key={`TimelineEvent${items.key}`} title="" icon={<i className={iconClass}></i>} iconColor="#006ba6">
                            <Collapsible trigger={<CollapsibleVisitHeader header={items.key} open={isOpen} />} open={isOpen}>
                                <ul className="posh-select">
                                    {items.value.length > 0 ? items.value.map(ss => (<SessionOption key={`SessionItem${ss.id}`} sessionItemId={ss.id} sessionItemLabel={ss.label} disabled={!subject.RandomisedStatus && ss.afterRandomisation} />)) : <Alert show={true} category={AlertCategoryEnum.Info}>No available sessions</Alert>}
                                </ul>
                            </Collapsible>
                        </TimelineEvent>)
                    })
                    }
                </>
            );
        } else {
            return null;
        }
    }

    const RollingVisitItemWrapper = () => {
        if (rollingVisitItems) {
            if (rollingVisitItems.length === 0)
                return null;

            const iconClass = "fas fa-calendar-star";

            return (
                <>
                    <TimelineEvent key={`TimelineEventRolling`} title="" icon={<i className={iconClass}></i>} iconColor="#006ba6">
                        <h3>Rolling Sessions</h3>
                        <ul className="posh-select">
                            {rollingVisitItems.length > 0 ? rollingVisitItems.map(ss => (<SessionOption key={`SessionItem${ss.id}`} sessionItemId={ss.id} sessionItemLabel={ss.label} disabled={!subject.RandomisedStatus && ss.afterRandomisation} />)) : <Alert show={true} category={AlertCategoryEnum.Info}>No available sessions</Alert>}
                        </ul>
                    </TimelineEvent>
                </>
            );
        } else {
            return null;
        }
    }

    React.useEffect(() => {
        const fetchValidDependentSessions = async (sessions: StudyScheduleSession[]): Promise<StudyScheduleSession[]> => {
            const postSessions = sessions.filter(x => x.preStudyScheduleSessionId);

            if (postSessions.length === 0)
                return [];

            const result = await legacyClient.getGenericSessions({
                Members: ["SessionScheduleId"],
                Filters: [
                    { Member: "Id", Values: postSessions.map(p => p.preStudyScheduleSessionId) }
                ]
            });

            const validSessions: StudyScheduleSession[] = [];
            for (const session of postSessions) {
                if (result.Data.map(x => x.SessionScheduleId).includes(session.preStudyScheduleSessionId)) {
                    validSessions.push(session);
                }
            }

            return validSessions;
        }

        const getSchedule = async () => {
            const filterNotFalidschedules = async (scheduleSessions) => {
                const standardSessions = scheduleSessions.filter(x => !x.preStudyScheduleSessionId);
                const validDependentSessions = await fetchValidDependentSessions(scheduleSessions);

                const availableSessions = [...standardSessions, ...validDependentSessions]
                    .sort((a, b) => a.ordinal - b.ordinal);

                return availableSessions;
            }

            const buildState = async (currentVisit: StudyScheduleSession[], nextVisits: KeyedVisitSessionItems[], unscheduledVisits: KeyedVisitSessionItems[], rollingVisits: StudyScheduleSession[]) => {
                for (const visit of nextVisits) {
                    visit.value = await filterNotFalidschedules(visit.value)
                }
                if (unscheduledVisits.length > 0) {
                    for (const visit of unscheduledVisits) {
                        visit.value = await filterNotFalidschedules(visit.value)
                    }
                    onSettingFutureVisitItems([...nextVisits, ...unscheduledVisits]);
                } else {
                    onSettingFutureVisitItems(nextVisits);
                }

                const allNextSessionIds = nextVisits.map(v => v.value).flat().map(s => s.id);
                const allCurrentSessionIds = currentVisit.map(s => s.id);
                const allUnscheduledIds = unscheduledVisits.map(s => s.value).flat().map(s => s.id);
                const allRollingIds = rollingVisits.map(s => s.id);

                onSettingCandidates([...allCurrentSessionIds, ...allNextSessionIds, ...allUnscheduledIds, ...allRollingIds]);
                setLoading(false);
            }

            const toKeyed = async (visit: StudyScheduleVisit): Promise<KeyedVisitSessionItems> => {
                if (!visit)
                    return null;

                return { key: visit.label, phase: visit.phaseId as Phases, value: visit.studyScheduleSessions.sort((a, b) => a.ordinal - b.ordinal) };
            }



            setLoading(true);
            try {
                const outstanding = (await legacyClient.getAvailableSubjectVisits(subject.Id, sessionType));
                const rollingSessions = await client.client.getStudyScheduleSessions(undefined, `rolling eq true and disabled eq false and studyScheduleVisit/studySchedule/studyId eq ${study.id} and sessionTypeId eq '${sessionType}'`, undefined, "studyScheduleVisit($expand=studySchedule)");

                if (outstanding.length === 0 && rollingSessions["@odata.count"] === 0) {
                    setAlert("There are no sessions available for selection");
                    setLoading(false);
                    return;
                }

                if (outstanding.length > 0) {
                    const outstandingSorted = outstanding.sort((a, b) => a.ordinal - b.ordinal);
                    onSettingOutstandingVisits(outstandingSorted);

                    const currentVisit = outstandingSorted[0];
                    const unscheduledVisitsSorted = outstandingSorted.filter(o => o.phaseId === Phases.Unscheduled);
                    const nextVisitsSorted = outstandingSorted.slice(1).filter(o => o.phaseId !== Phases.Unscheduled);

                    const currentSessionScheduleResult = await client.client.getStudyScheduleSessions(undefined, `disabled eq false and sessionTypeId eq '${sessionType}' and id in (${currentVisit.studyScheduleSessions.map(s => s.id).join(",")})`)


                    const availableSessions = await filterNotFalidschedules(currentSessionScheduleResult.value);

                    onSettingCurrentVisitItems(availableSessions);

                    onSettingRollingVisitItems(rollingSessions.value);
                    if (nextVisitsSorted) {
                        let nextVisits: KeyedVisitSessionItems[] = [];
                        let unscheduledVisits = [];

                        for (const visit of unscheduledVisitsSorted) {
                            const keyedVisit = await toKeyed(visit);

                            unscheduledVisits = [...unscheduledVisits, keyedVisit];
                        }

                        for (const visit of nextVisitsSorted) {

                            const keyedVisit = await toKeyed(visit);

                            const mandatorySessions = keyedVisit.value.filter(x => x.mandatory);

                            nextVisits = [...nextVisits, keyedVisit];

                            if (mandatorySessions.length >= 1) {
                                await buildState(availableSessions, nextVisits, unscheduledVisits, rollingSessions.value);
                                return;
                            }
                        }

                        onSettingFutureVisitItems(nextVisits);
                        await buildState(availableSessions, nextVisits, unscheduledVisits, rollingSessions.value);
                    }

                } else {
                    onSettingRollingVisitItems(rollingSessions.value);
                    await buildState([], [], [], rollingSessions.value);
                }

            } catch (ex) {
                if (ex) {
                    setAlert(ex.Message);
                } else {
                    setAlert("Visit Schedule failed to load");
                }

                setLoading(false);
                return;
            }
        }

        getSchedule();
    }, []);

    if (loading) return (<div className="modal-body"><LoadingWithMessage message="Loading Available Sessions..." /></div>);

    if (alert) return (
        <>
            <div className="modal-body">
                <Alert category={AlertCategoryEnum.Danger} show={true}>{alert}</Alert>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary btn-outline" onClick={() => history.goBack()}>Back</button>
            </div>
        </>);

    return (
        <>
            <div className="modal-body">
                <Timeline style={{ fontSize: 100 + '%' }}>
                    <TimelineEvent title="" icon={<i className="fas fa-calendar-edit"></i>} iconColor="#006ba6">
                        <h3>Current Visit</h3>
                        {currentVisitItems.length !== 0 ?
                            <ul className="posh-select">
                                {currentVisitItems.map(ss => (<SessionOption key={`SessionItem${ss.id}`} sessionItemId={ss.id} sessionItemLabel={ss.label} disabled={!subject.RandomisedStatus && ss.afterRandomisation} />))}
                            </ul>
                            : <Alert show={true} category={AlertCategoryEnum.Danger}>There are no scheduled sessions for this session type</Alert>
                        }
                    </TimelineEvent>
                    <FutureVisitItemWrapper />
                    <RollingVisitItemWrapper />
                </Timeline>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" disabled={selectedItems.length === 0} onClick={() => onStartSession()}>Start Session</button>
                <button className="btn btn-primary btn-outline" onClick={() => history.goBack()}>Back</button>
            </div>
        </>
    );
}
